import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { usePreventBack } from '../../hooks/usePreventBack';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import {
  UserIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  ChartBarIcon,
  PlayIcon,
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  ExclamationCircleIcon,
  SignalIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  VideoCameraIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'src/components/ui/ui/tooltip';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';
import { sanitizeReturnUrl } from '../../utils/sanitizeUrl';
import { buildAssessmentFlowSearch } from '../../lib/publicAssessmentFlow';
import { useScreeningExpiryRedirect } from '../../hooks/useScreeningExpiryRedirect';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import { useCandidateLanguage } from '../../i18n/candidate';

const STEP_SECONDARY = {
  'Basic details': 'Share your contact information',
  'Resume upload': 'Upload your latest resume',
  'Assessment': 'Complete skills assessment',
  'AI interview': 'Answer structured questions',
  'Document upload': 'Upload supporting documents (optional)',
};

const getStepMeta = (interviewType) => ({
  'Basic details': { icon: UserIcon, desc: '', gradient: 'from-blue-500 to-indigo-600' },
  'Resume upload': { icon: DocumentTextIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'Assessment': { icon: AcademicCapIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'AI interview': {
    icon: interviewType === 'video' ? VideoCameraIcon : MicrophoneIcon,
    desc: '',
    gradient: 'from-orange-500 to-rose-600',
  },
  'Document upload': { icon: DocumentTextIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'Application form': { icon: ClipboardDocumentListIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
});

function getInterviewStepLabel(interviewType) {
  if (interviewType === 'video') return 'AI video interview';
  if (interviewType === 'voice' || interviewType === 'audio') return 'AI voice interview';
  return 'AI interview';
}

function isPassageReadingQuestion(question) {
  if (!question) return false;
  const rawType = typeof question.type === 'string' ? question.type : '';
  const normalized = rawType.trim().toLowerCase();
  if (!normalized && question.passageMeta?.passageText) return true;
  if (normalized === 'passage-reading') return true;
  if (normalized.replace(/\s+/g, '-') === 'passage-reading') return true;
  if (normalized.includes('passage') && normalized.includes('reading')) return true;
  return Boolean(question.passageMeta?.passageText);
}

function getAssessmentQuestionCount(assessment) {
  if (!assessment) return 0;
  if (assessment.noOfQuestions != null && assessment.noOfQuestions > 0) {
    return assessment.noOfQuestions;
  }
  return assessment.content?.customQuestions?.length ?? assessment.totalQuestions ?? 0;
}

const PRESTART_DEADLINE_STORAGE_KEY = 'tc_assessment_prestart_deadline_v1';

function readPrestartDeadlineMs(sharingLink, emailLower) {
  if (!sharingLink || !emailLower) return null;
  try {
    const raw = sessionStorage.getItem(PRESTART_DEADLINE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const expectedKey = `${sharingLink}|${emailLower}`;
    if (!parsed || parsed.key !== expectedKey || typeof parsed.deadline !== 'number') return null;
    return parsed.deadline;
  } catch {
    return null;
  }
}

function writePrestartDeadlineMs(sharingLink, emailLower, deadlineMs) {
  if (!sharingLink || !emailLower) return;
  try {
    sessionStorage.setItem(
      PRESTART_DEADLINE_STORAGE_KEY,
      JSON.stringify({ key: `${sharingLink}|${emailLower}`, deadline: deadlineMs })
    );
  } catch {
    /* ignore quota */
  }
}

function clearPrestartDeadline(sharingLink, emailLower) {
  if (!sharingLink || !emailLower) return;
  try {
    const raw = sessionStorage.getItem(PRESTART_DEADLINE_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed?.key === `${sharingLink}|${emailLower}`) {
      sessionStorage.removeItem(PRESTART_DEADLINE_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

const AssessmentJoin = () => {
  const { sharingLink } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnUrl = sanitizeReturnUrl(searchParams.get('returnUrl') || location.state?.returnUrl);
  const screeningIdFromQuery = searchParams.get('screeningId') || location.state?.screeningId || null;
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  usePreventBack(!!screeningIdFromQuery, { onBackAttempt: useCallback(() => setShowLeaveModal(true), []) });
  const prefilledEmail = searchParams.get('email') || '';
  const prefilledName = searchParams.get('name') || '';
  const hasInterviewFromQuery = searchParams.get('hasInterview') === 'true';
  const interviewSharingLinkFromQuery = (searchParams.get('interviewSharingLink') || '').trim();
  const interviewTypeFromQuery = (searchParams.get('interviewType') || '').toLowerCase();
  const isEmailLocked = Boolean(prefilledEmail);
  const isNameLocked = Boolean(prefilledName);

  const [assessment, setAssessment] = useState(null);
  const t = useCandidateLanguage({ screeningId: screeningIdFromQuery, assessmentId: assessment?._id });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidateInfo, setCandidateInfo] = useState({
    name: '',
    email: '',
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [micPermission, setMicPermission] = useState('unknown'); // 'unknown' | 'granted' | 'denied' | 'requesting'
  const [proctoringConfig, setProctoringConfig] = useState(null);
  const [cameraPermission, setCameraPermission] = useState('unknown'); // 'unknown' | 'granted' | 'denied' | 'requesting'
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(10 * 60);
  const autoVerifyAttemptedRef = useRef(false);
  const prestartTimerInitializedRef = useRef(false);
  useScreeningExpiryRedirect({
    screeningId: screeningIdFromQuery,
    email: searchParams.get('email') || location.state?.candidateInfo?.email || candidateInfo?.email || '',
    enabled: !!screeningIdFromQuery,
  });
  // Pre-start timer (10 minutes). When it expires we disable actions on this page.
  const timerExpired = timeLeftSeconds <= 0;

  useScreeningLeaveWarning(!!screeningIdFromQuery);
  // Do NOT use useScreeningSessionEndBeacon here: refresh/pagehide would call
  // /candidate-session-end and force-complete the assessment before they click Start.

  useEffect(() => {
    prestartTimerInitializedRef.current = false;
  }, [sharingLink]);

  // Restore "start within 10 minutes" across refresh when email is on the URL (screening flow).
  useEffect(() => {
    if (!assessment || prestartTimerInitializedRef.current) return;
    const emailFromUrl = (searchParams.get('email') || '').trim().toLowerCase();
    if (!emailFromUrl) {
      prestartTimerInitializedRef.current = true;
      return;
    }
    prestartTimerInitializedRef.current = true;
    const existing = readPrestartDeadlineMs(sharingLink, emailFromUrl);
    if (existing != null) {
      if (existing <= Date.now()) {
        setTimeLeftSeconds(0);
      } else {
        setTimeLeftSeconds(Math.max(0, Math.ceil((existing - Date.now()) / 1000)));
      }
      return;
    }
    const deadline = Date.now() + 10 * 60 * 1000;
    writePrestartDeadlineMs(sharingLink, emailFromUrl, deadline);
    setTimeLeftSeconds(10 * 60);
  }, [assessment, sharingLink, searchParams]);

  // Countdown effect – only once assessment metadata is available
  useEffect(() => {
    if (!assessment || timerExpired) return;
    const interval = setInterval(() => {
      setTimeLeftSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [assessment, timerExpired]);

  useEffect(() => {
    if (timerExpired && screeningIdFromQuery) {
      toast.error(t('Your session expired. Please confirm your details to restart.'), { id: 'local-timeout' });
      const urlParams = new URLSearchParams();
      const emailParam = searchParams.get('email') || candidateInfo?.email;
      const nameParam = searchParams.get('name') || candidateInfo?.name;
      
      if (emailParam && screeningIdFromQuery) {
        axios.post(`/api/screenings/${screeningIdFromQuery}/local-timeout`, { email: emailParam.trim() }).catch(err => console.error('Failed to report local timeout:', err));
        clearPrestartDeadline(sharingLink, emailParam.trim().toLowerCase());
      }
      
      if (emailParam) urlParams.set('email', emailParam.trim());
      if (nameParam) urlParams.set('name', nameParam.trim());
      navigate(`/apply/${screeningIdFromQuery}?${urlParams.toString()}`, { replace: true });
    }
  }, [timerExpired, screeningIdFromQuery, navigate, candidateInfo, searchParams, sharingLink, t]);

  const fetchAssessment = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/assessments/share/${sharingLink}`);
      logger.log('Assessment data received:', response.data.assessment);
      logger.log('Invited candidates:', response.data.assessment.invitedCandidates);
      setAssessment(response.data.assessment);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      if (error.response?.status === 404) {
        setError('Assessment not found or no longer available');
      } else {
        setError('Failed to load assessment. Please check the link and try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [sharingLink]);

  useEffect(() => {
    if (sharingLink) {
      fetchAssessment();
    }
  }, [sharingLink, fetchAssessment]);

  // Prefill candidate info from URL (hub link) or from navigation state (screening apply)
  useEffect(() => {
    const email = searchParams.get('email') || '';
    const name = searchParams.get('name') || '';
    const fromUrl = { ...(email && { email }), ...(name && { name }) };
    const fromState = location.state?.candidateInfo || {};
    setCandidateInfo((prev) => ({
      ...prev,
      ...fromUrl,
      ...fromState,
    }));
  }, [searchParams, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If name/email were prefilled via URL, do not allow editing.
    if (name === 'email' && isEmailLocked) return;
    if (name === 'name' && isNameLocked) return;

    setCandidateInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const verifyAndStart = useCallback(
    async (email, name) => {
      if (!assessment) return false;
      const emailTrim = email.trim().toLowerCase();
      const nameTrim = name.trim();
      if (!emailTrim || !nameTrim) return false;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailTrim)) {
        setError('Please enter a valid email address');
        return false;
      }

      try {
        const res = await axios.post(`/api/assessments/${assessment._id}/candidate-start`, {
          email: emailTrim,
        });
        
        // Inject questionSnapshot so AssessmentTake can read it from invitedCandidates
        if (assessment.isRubricBased && res.data.questionSnapshot) {
          if (!assessment.invitedCandidates) assessment.invitedCandidates = [];
          const inv = assessment.invitedCandidates.find(c => String(c.email || '').toLowerCase() === emailTrim);
          if (inv) {
            inv.questionSnapshot = res.data.questionSnapshot;
          } else {
            // No pre-existing entry (public link flow) — push a stub so AssessmentTake can find it
            assessment.invitedCandidates.push({ email: emailTrim, questionSnapshot: res.data.questionSnapshot });
          }
        }
        
        setCandidateInfo((prev) => ({ ...prev, email: emailTrim, name: nameTrim }));
        setIsAuthenticated(true);
        return true;
      } catch (err) {
        if (err.response?.data?.status === 'already_completed') {
          setError('You have already completed this assessment. You cannot take it again.');
        } else if (err.response?.data?.status === 'already_started') {
          setCandidateInfo((prev) => ({ ...prev, email: emailTrim, name: nameTrim }));
          setIsAuthenticated(true);
          setError(null);
          toast.success(t('Welcome back — you can continue from this step.'), { duration: 4000 });
          return true;
        } else if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError('Failed to start assessment. Please try again.');
        }
        return false;
      }
    },
    [assessment, t]
  );

  const handleAuthenticate = async (e) => {
    e.preventDefault();
    if (screeningIdFromQuery) {
      try {
        const email =
          searchParams.get('email') || candidateInfo?.email?.trim() || location.state?.candidateInfo?.email || '';
        const params = new URLSearchParams();
        if (email) params.set('email', email.trim().toLowerCase());
        const res = await axios.get(
          `/api/screenings/${screeningIdFromQuery}/apply-info${params.toString() ? `?${params.toString()}` : ''}`
        );
        const payload = res.data || {};
        const deadline = payload.screeningDeadline || null;
        const timeUp = !!payload.timeUp;
        const parsed = deadline ? Date.parse(deadline) : NaN;
        const expiredFromClock = deadline ? !Number.isNaN(parsed) && Date.now() >= parsed : false;
        if (timeUp || expiredFromClock) {
          setError('Your screening window has ended. You can no longer start this assessment.');
          return;
        }
      } catch (_) {
        // Non-blocking: allow attempt; server will enforce if time is actually up.
      }
    }
    if (!candidateInfo.name.trim() || !candidateInfo.email.trim()) {
      setError('Please provide both name and email');
      return;
    }
    setAuthenticating(true);
    setError(null);
    try {
      const ok = await verifyAndStart(candidateInfo.email, candidateInfo.name);
      if (!ok) return;
    } finally {
      setAuthenticating(false);
    }
  };

  // When coming from Resume upload (name + email in URL), auto-verify and skip the form
  useEffect(() => {
    if (loading || !assessment || autoVerifyAttemptedRef.current) return;
    const email = searchParams.get('email')?.trim();
    const name = searchParams.get('name')?.trim();
    if (!email || !name) return;

    autoVerifyAttemptedRef.current = true;
    setCandidateInfo((prev) => ({ ...prev, email, name }));
    setAuthenticating(true);
    setError(null);
    verifyAndStart(email, name).finally(() => setAuthenticating(false));
  }, [loading, assessment, searchParams, verifyAndStart]);

  // Detect if assessment has passage-reading questions
  const hasPassageReading = React.useMemo(() => {
    if (!assessment) return false;
    
    // Check custom questions
    const customs = assessment.content?.customQuestions || [];
    if (customs.some((q) => isPassageReadingQuestion(q))) return true;

    // Check library modules
    if (assessment.modules && Array.isArray(assessment.modules)) {
      return assessment.modules.some((mod) => {
        const test = mod?.libraryTestId || { title: mod?.title, estimatedTime: mod?.duration, questions: mod?.questionBank };
        if (!test || !Array.isArray(test.questions)) return false;
        return test.questions.some((q) => isPassageReadingQuestion(q.question));
      });
    }

    return false;
  }, [assessment]);

  // Request microphone permission
  const requestMicPermission = useCallback(async () => {
    setMicPermission('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicPermission('granted');
      return true;
    } catch (err) {
      console.error('Mic permission denied:', err);
      setMicPermission('denied');
      return false;
    }
  }, []);

  useEffect(() => {
    if (!hasPassageReading) return;
    if (micPermission !== 'unknown') return;
    if (!navigator?.mediaDevices?.getUserMedia) {
      setMicPermission('denied');
      return;
    }
    requestMicPermission();
  }, [hasPassageReading, micPermission, requestMicPermission]);

  // Load public proctoring configuration
  useEffect(() => {
    if (!assessment?._id) return;
    const fetchProctoringConfig = async () => {
      try {
        const res = await axios.get(`/api/proctoring/public/config/${assessment._id}`);
        setProctoringConfig(res.data?.config || null);
      } catch (err) {
        console.error('Failed to fetch proctoring config:', err);
      }
    };
    fetchProctoringConfig();
  }, [assessment?._id]);

  // Detect if camera monitoring is enabled
  const hasCameraMonitoring = React.useMemo(() => {
    return proctoringConfig?.cameraMonitoring?.enabled === true;
  }, [proctoringConfig]);

  // Request camera permission
  const requestCameraPermission = useCallback(async () => {
    setCameraPermission('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
      setCameraPermission('granted');
      return true;
    } catch (err) {
      console.error('Camera permission denied:', err);
      setCameraPermission('denied');
      return false;
    }
  }, []);

  useEffect(() => {
    if (!hasCameraMonitoring) return;
    if (cameraPermission !== 'unknown') return;
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraPermission('denied');
      return;
    }
    requestCameraPermission();
  }, [hasCameraMonitoring, cameraPermission, requestCameraPermission]);

  const startAssessment = () => {
    const emailForKey = (
      candidateInfo?.email?.trim() ||
      searchParams.get('email')?.trim() ||
      ''
    ).toLowerCase();
    clearPrestartDeadline(sharingLink, emailForKey);
    const takeSearch = buildAssessmentFlowSearch({
      email: candidateInfo?.email || searchParams.get('email'),
      name: candidateInfo?.name || searchParams.get('name'),
      screeningId: screeningIdFromQuery,
      returnUrl,
      hasInterview: hasInterviewFromQuery,
      interviewSharingLink: interviewSharingLinkFromQuery,
    });
    navigate(`/assessment/${sharingLink}/take${takeSearch}`, {
      state: {
        assessment,
        candidateInfo,
        returnUrl,
        hasInterview: hasInterviewFromQuery,
        screeningId: screeningIdFromQuery,
        interviewSharingLink: interviewSharingLinkFromQuery || undefined,
      },
    });
  };

  // Try to reflect screening configuration: if resume scoring is disabled, omit "Resume upload" from timeline.
  const [hasResumeScoring, setHasResumeScoring] = React.useState(true);
  const [resumeStepResolved, setResumeStepResolved] = React.useState(!screeningIdFromQuery);
  const [includeDocumentCollection, setIncludeDocumentCollection] = React.useState(false);
  const [includeApplicationForm, setIncludeApplicationForm] = React.useState(false);
  useEffect(() => {
    if (!screeningIdFromQuery) {
      setHasResumeScoring(false);
      setResumeStepResolved(true);
      setIncludeDocumentCollection(false);
      return;
    }
    let cancelled = false;
    setResumeStepResolved(false);
    const email =
      searchParams.get('email')?.trim().toLowerCase() || candidateInfo?.email?.trim().toLowerCase();
    const params = new URLSearchParams();
    if (email) params.set('email', email);
    axios
      .get(`/api/screenings/${screeningIdFromQuery}/apply-info${params.toString() ? `?${params.toString()}` : ''}`)
      .then((res) => {
        if (cancelled) return;
        const payload = res.data || {};
        setHasResumeScoring(payload.hasResumeScoring !== false);
        setIncludeDocumentCollection(!!payload.includeDocumentCollection);
        setIncludeApplicationForm(!!payload.includeApplicationForm);
        setResumeStepResolved(true);
      })
      .catch(() => {
        if (!cancelled) {
          setHasResumeScoring(false);
          setIncludeDocumentCollection(false);
          setResumeStepResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [screeningIdFromQuery, searchParams, candidateInfo?.email, location.state]);

  const steps = [
    'Basic details',
    ...(hasResumeScoring && resumeStepResolved ? ['Resume upload'] : []),
    ...(includeDocumentCollection ? ['Document upload'] : []),
    ...(includeApplicationForm ? ['Application form'] : []),
    'Assessment',
    ...(hasInterviewFromQuery ? ['AI interview'] : []),
  ];
  const currentStepIndex = steps.findIndex((s) => s === 'Assessment');
  const stepMetaMap = getStepMeta(interviewTypeFromQuery === 'video' ? 'video' : 'voice');

  const compactHeader = (
    <header className="flex items-center justify-between gap-3">
      <div className="flex flex-col min-w-0">
        <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('Powered by')}</p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <img src="/logo.svg" alt="TalentCrux" className="h-9 w-9 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0" />
          <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">TalentCrux</span>
        </div>
      </div>
      <div className="hidden sm:flex items-center justify-end flex-shrink-0 min-w-0" aria-hidden />
    </header>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}
        </div>
        <div className="mx-auto max-w-3xl flex items-center justify-center mt-8 sm:mt-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-indigo-600 mx-auto mb-3 sm:mb-4"></div>
            <p className="text-xs text-gray-600 sm:text-sm">{t('Loading assessment...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}
          <div className="mt-4 sm:mt-8 max-w-md mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8 text-center">
              <div className="bg-red-100 rounded-full p-2.5 sm:p-3 mx-auto mb-3 sm:mb-4 w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">
                <DocumentTextIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1.5 sm:mb-2 sm:text-xl">{t('Assessment Not Available')}</h2>
              <p className="text-xs text-gray-600 mb-4 sm:mb-6 sm:text-sm">{t(error)}</p>
              <button
                onClick={() => window.history.back()}
                data-testid="start-assessment-btn"
                className="inline-flex items-center justify-center rounded-lg bg-gray-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors sm:px-6 sm:py-2.5"
              >
                {t('Go Back')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}
          <div className="mt-4 sm:mt-8 max-w-md mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8 text-center">
              <h2 className="text-lg font-bold text-gray-900 mb-1.5 sm:mb-2 sm:text-xl">{t('Assessment Not Found')}</h2>
              <p className="text-xs text-gray-600 sm:text-sm">
                {t("The assessment you're looking for doesn't exist or is no longer available.")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const canSubmit = candidateInfo.name.trim() && candidateInfo.email.trim();
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}

          {steps.length > 0 && (
            <section className="mt-6 sm:mt-8">
              <p className="text-center text-[11px] sm:text-sm font-semibold uppercase tracking-[0.22em] text-gray-500 mb-5 sm:mb-7">
                {t('Your screening journey')}
              </p>
              <div className="flex items-start justify-center gap-0">
                {steps.map((label, index) => {
                  const meta = getStepMeta('video')[label] || { icon: CheckIcon, desc: STEP_SECONDARY[label] || '', gradient: 'from-gray-400 to-gray-500' };
                  const StepIcon = meta.icon;
                  const isActive = index === currentStepIndex;
                  const isCompleted = index < currentStepIndex;
                  return (
                    <React.Fragment key={label}>
                      <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                        <span
                          className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.16em] whitespace-nowrap mb-1.5 ${
                            isActive ? 'text-electric-blue' : isCompleted ? 'text-emerald-400' : 'text-gray-300'
                          }`}
                        >
                          {t('Step {number}', { number: index + 1 })}
                        </span>
                        <div
                          className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl transition-all duration-500 ${
                            isActive
                              ? `bg-gradient-to-br ${meta.gradient} text-white shadow-lg`
                              : isCompleted
                              ? 'bg-emerald-50 text-emerald-500 border-2 border-emerald-200'
                              : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'
                          }`}
                          style={
                            isActive
                              ? { boxShadow: '0 8px 25px rgba(0, 123, 255, 0.2)' }
                              : isCompleted
                              ? { boxShadow: '0 6px 18px rgba(16, 185, 129, 0.18)' }
                              : {}
                          }
                        >
                          {isCompleted ? (
                            <CheckIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.5} />
                          ) : (
                            <StepIcon className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={isActive ? 2 : 1.5} />
                          )}
                          {isActive && (
                            <span
                              className="absolute inset-0 rounded-2xl border-2 border-electric-blue/30 animate-ping"
                              style={{ animationDuration: '2s' }}
                            />
                          )}
                        </div>
                        <p
                          className={`mt-2 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${
                            isActive ? 'text-gray-900' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
                          }`}
                        >
                          {t(label === 'AI interview' ? getInterviewStepLabel(interviewTypeFromQuery) : label)}
                        </p>
                      </div>
                      {index < steps.length - 1 && (
                        <div className="flex-1 flex items-center pt-[52px] sm:pt-[52px] min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                          <div className="w-full flex items-center gap-0.5">
                            {[...Array(4)].map((_, i) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <div
                                key={i}
                                className={`flex-1 h-0.5 rounded-full ${
                                  index < currentStepIndex ? 'bg-emerald-400/60' : 'bg-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </section>
          )}

          {authenticating && prefilledName && prefilledEmail ? (
            <div className="mt-4 sm:mt-6 max-w-2xl mx-auto flex flex-col items-center justify-center py-8 sm:py-12" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-2 border-electric-blue border-t-transparent" aria-hidden />
              <p className="mt-2 sm:mt-3 text-xs font-medium text-gray-600 sm:text-sm">{t('Verifying your information…')}</p>
            </div>
          ) : (
          <div className="mt-4 sm:mt-6 max-w-2xl mx-auto space-y-3 sm:space-y-4">
            {/* Brief prep hint – compact on mobile */}
            <div className="rounded-xl border border-electric-blue-100 bg-electric-blue-50 px-3 py-2.5 sm:px-4 sm:py-3">
              <div className="flex gap-2.5 sm:gap-3">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-lg bg-electric-blue-100">
                  <SparklesIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-electric-blue" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Before you begin')}</h3>
                  <ul className="mt-1.5 sm:mt-2 space-y-1 sm:space-y-1.5 text-xs text-gray-700 leading-relaxed sm:text-sm">
                    <li className="flex gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-electric-blue" />
                      <span>{t('Read each question carefully and answer confidently.')}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-electric-blue" />
                      <span>{t('This session is monitored. No external help, tab switching, or extra devices.')}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-electric-blue" />
                      <span>{t("Give it your best. We're rooting for you!")}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Compact: assessment info + form in one card; compact on mobile */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5 lg:p-6">
              <h3 className="text-sm font-bold text-gray-900 tracking-tight sm:text-base">{assessment.title}</h3>
              <div className="mt-2 sm:mt-3 grid grid-cols-2 gap-x-3 gap-y-2 sm:gap-x-4 sm:gap-y-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-electric-blue-50 text-electric-blue">
                    <UserIcon className="h-3.5 w-3.5" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{t('Type')}</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{assessment.questionType ? t(assessment.questionType) : assessment.role || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                    <ClockIcon className="h-3.5 w-3.5" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{t('Duration')}</p>
                    <p className="text-sm font-semibold text-gray-900">{t('{minutes} min', { minutes: assessment.duration })}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-lavender-purple-50 text-lavender-purple">
                    <QuestionMarkCircleIcon className="h-3.5 w-3.5" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{t('Questions')}</p>
                    <p className="text-sm font-semibold text-gray-900">{getAssessmentQuestionCount(assessment)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <ChartBarIcon className="h-3.5 w-3.5" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{t('Points')}</p>
                    <p className="text-sm font-semibold text-gray-900">{assessment.totalPoints || 0}</p>
                  </div>
                </div>
              </div>
              {assessment.instructions && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{t('Instructions')}</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{assessment.instructions}</p>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-100 sm:mt-4 sm:pt-4">
                <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:mb-1 sm:text-sm">{t('Enter your information')}</h3>
                <p className="text-[11px] text-gray-600 mb-2 sm:mb-3 sm:text-xs">{t('We need your name and email to verify your invite.')}</p>

                <form onSubmit={handleAuthenticate} className="space-y-2.5 sm:space-y-3" aria-describedby={error ? 'assessment-form-error' : undefined}>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Full name')}<span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <UserIcon className="h-5 w-5" />
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={candidateInfo.name}
                      onChange={handleInputChange}
                      readOnly={isNameLocked}
                      required
                      className={`block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue sm:py-2.5 ${
                        isNameLocked ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      data-testid="candidate-name-input"
                      placeholder={t('Your full name')}
                      title={isNameLocked ? t('Prefilled from the link and cannot be changed.') : undefined}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Email')}<span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <EnvelopeIcon className="h-5 w-5" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={candidateInfo.email}
                      onChange={handleInputChange}
                      readOnly={isEmailLocked}
                      required
                      className={`block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue sm:py-2.5 ${
                        isEmailLocked ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      data-testid="candidate-email-input"
                      placeholder="you@example.com"
                      title={isEmailLocked ? t('Prefilled from the link and cannot be changed.') : undefined}
                    />
                  </div>
                </div>

                {error && (
                  <div id="assessment-form-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 sm:text-sm" role="alert">
                    {t(error)}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="candidate-ready-btn"
                  disabled={authenticating || !canSubmit || timerExpired}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-electric-blue px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-electric-blue-600 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 focus:ring-offset-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors sm:py-2.5"
                  aria-busy={authenticating}
                >
                  {timerExpired ? (
                    <>{t("Time's up")}</>
                  ) : authenticating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" aria-hidden />
                      {t('Verifying…')}
                    </>
                  ) : (
                    <>
                      {t('Continue to Assessment')}
                      <ArrowRightIcon className="h-4 w-4" aria-hidden />
                    </>
                  )}
                </button>
              </form>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    );
  }

  // Authenticated view - show assessment start screen
  return (
    <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={() => { setShowLeaveModal(false); navigate(-1); }}
        title={t('Leave this page?')}
        message={t('Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out.')}
      />
      <div className="mx-auto max-w-6xl">
        {compactHeader}

        {steps.length > 0 && (
          <section className="mt-6 sm:mt-8">
            <p className="text-center text-[11px] sm:text-sm font-semibold uppercase tracking-[0.22em] text-gray-500 mb-5 sm:mb-7">
              {t('Your screening journey')}
            </p>
            <div className="flex items-start justify-center gap-0">
              {steps.map((label, index) => {
                const meta = getStepMeta('video')[label] || { icon: CheckIcon, desc: STEP_SECONDARY[label] || '', gradient: 'from-gray-400 to-gray-500' };
                const StepIcon = meta.icon;
                const isActive = index === currentStepIndex;
                const isCompleted = index < currentStepIndex;
                return (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                      <span
                        className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.16em] whitespace-nowrap mb-1.5 ${
                          isActive ? 'text-electric-blue' : isCompleted ? 'text-emerald-400' : 'text-gray-300'
                        }`}
                      >
                        {t('Step {number}', { number: index + 1 })}
                      </span>
                      <div
                        className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl transition-all duration-500 ${
                          isActive
                            ? `bg-gradient-to-br ${meta.gradient} text-white shadow-lg`
                            : isCompleted
                            ? 'bg-emerald-50 text-emerald-500 border-2 border-emerald-200'
                            : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'
                        }`}
                        style={
                          isActive
                            ? { boxShadow: '0 8px 25px rgba(0, 123, 255, 0.2)' }
                            : isCompleted
                            ? { boxShadow: '0 6px 18px rgba(16, 185, 129, 0.18)' }
                            : {}
                        }
                      >
                        {isCompleted ? (
                          <CheckIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.5} />
                        ) : (
                          <StepIcon className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={isActive ? 2 : 1.5} />
                        )}
                        {isActive && (
                          <span
                            className="absolute inset-0 rounded-2xl border-2 border-electric-blue/30 animate-ping"
                            style={{ animationDuration: '2s' }}
                          />
                        )}
                      </div>
<p
                          className={`mt-2 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${
                            isActive ? 'text-gray-900' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
                          }`}
                        >
                        {t(label === 'AI interview' ? getInterviewStepLabel(interviewTypeFromQuery) : label)}
                        </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="flex-1 flex items-center pt-[52px] sm:pt-[52px] min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                        <div className="w-full flex items-center gap-0.5">
                          {[...Array(4)].map((_, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div
                              key={i}
                              className={`flex-1 h-0.5 rounded-full ${
                                index < currentStepIndex ? 'bg-emerald-400/60' : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </section>
        )}

        {/* Main card – compact on mobile */}
        <div className="mt-4 sm:mt-6 max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
            {/* Header strip */}
            <div className="bg-electric-blue px-5 py-4 sm:px-6 sm:py-5">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    {t('Start your assessment')}
                  </h1>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 sm:px-3.5 sm:py-1.5 text-[10px] sm:text-sm font-semibold text-electric-blue-700 shadow-sm cursor-default whitespace-nowrap">
                          <ClockIcon className="h-4 w-4 text-electric-blue-500" />
                          <span>
                            {timerExpired
                              ? t('Time up – window closed')
                              : t('Start within {time}', {
                                  time: `${Math.floor(timeLeftSeconds / 60)
                                    .toString()
                                    .padStart(2, '0')}:${(timeLeftSeconds % 60)
                                    .toString()
                                    .padStart(2, '0')}`,
                                })}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs text-xs leading-snug">
                        {t('You need to start the assessment within 10 minutes of opening this page. After that, your screening window may automatically close based on your deadline.')}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 lg:p-7">
              {/* Duration + key info row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-5 mb-4 sm:mb-6">
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                    <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                      {t('Takes about {minutes} mins', { minutes: assessment.estimatedTime || assessment.duration || 5 })}
                    </h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t('Complete the assessment in one sitting.')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                    <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                      {t('Be focused and confident')}
                    </h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t('Read each question carefully.')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                    <SignalIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                      {t('Quiet spot & strong Wi‑Fi')}
                    </h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t('Minimize distractions around you.')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">{t('Reviewed by a human')}</h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t('A recruiter will review your responses.')}
                    </p>
                  </div>
                </div>
              </div>
              {/* Module Overview */}
              {assessment.isMultiModule && !assessment.isRubricBased && assessment.modules?.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 mb-4 sm:p-4 sm:mb-6">
                  <h3 className="text-xs font-bold text-gray-900 mb-2 sm:text-sm">{t('Assessment Modules')}</h3>
                  <p className="text-[11px] sm:text-xs text-gray-500 mb-3">{t('This assessment is broken down into {count} parts. You will take them one by one.', { count: assessment.modules.length })}</p>
                  <div className="space-y-2">
                    {assessment.modules.map((mod, i) => (
                      <div key={i} className="flex items-center justify-between bg-white border border-gray-100 shadow-sm rounded-lg p-2.5 sm:p-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-electric-blue-50 text-electric-blue font-semibold text-xs">{i + 1}</span>
                          <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">{mod.title || mod.libraryTestId?.title || t('Module {number}', { number: i + 1 })}</span>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-3 text-[11px] sm:text-xs text-gray-500">
                          <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                            <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                            <span className="font-medium text-gray-600">{t('{minutes} mins', { minutes: mod.duration ?? mod.libraryTestId?.estimatedTime ?? 0 })}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Before you begin – amber warning */}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4 sm:p-5 sm:mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <ExclamationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <h3 className="text-sm font-bold text-amber-900">{t('Before you begin')}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-1">
                  <div className="flex gap-2.5 items-start">
                    <VideoCameraIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                      <strong>{t('Camera & Audio:')}</strong> {t('You will be recorded via webcam and microphone')}
                    </p>
                  </div>
                  <div className="flex gap-2.5 items-start">
                    <UserIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                      <strong>{t('Attire:')}</strong> {t('Please dress appropriately (formal or professional attire)')}
                    </p>
                  </div>
                  <div className="flex gap-2.5 items-start">
                    <SparklesIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                      <strong>{t('Environment:')}</strong> {t('Ensure a quiet, well-lit space with stable internet')}
                    </p>
                  </div>
                  <div className="flex gap-2.5 items-start">
                    <ClockIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                      <strong>{t('Important:')}</strong> {t('Start within 2 mins. Cannot be restarted once begun')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Microphone permission for passage reading */}
              {hasPassageReading && (
                <div className={`rounded-xl border p-3 mb-4 sm:p-4 sm:mb-6 ${
                  micPermission === 'granted'
                    ? 'border-emerald-200 bg-emerald-50'
                    : micPermission === 'denied'
                      ? 'border-red-200 bg-red-50'
                      : 'border-blue-200 bg-blue-50'
                }`}>
                  <div className="flex gap-2 sm:gap-2.5">
                    <MicrophoneIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      micPermission === 'granted'
                        ? 'text-emerald-600'
                        : micPermission === 'denied'
                          ? 'text-red-600'
                          : 'text-blue-600'
                    }`} />
                    <div className="flex-1">
                      <h3 className={`text-xs font-bold mb-1 sm:text-sm ${
                        micPermission === 'granted'
                          ? 'text-emerald-900'
                          : micPermission === 'denied'
                            ? 'text-red-900'
                            : 'text-blue-900'
                      }`}>
                        {micPermission === 'granted'
                          ? t('Microphone access granted')
                          : micPermission === 'denied'
                            ? t('Microphone access denied')
                            : t('Microphone access required')}
                      </h3>
                      <p className={`text-xs mb-2 sm:text-sm ${
                        micPermission === 'granted'
                          ? 'text-emerald-800'
                          : micPermission === 'denied'
                            ? 'text-red-800'
                            : 'text-blue-800'
                      }`}>
                        {micPermission === 'granted'
                          ? t('Your microphone is ready. You can now start the assessment.')
                          : micPermission === 'denied'
                            ? t('Please allow microphone access in your browser settings and refresh the page. This assessment includes a passage reading section that requires audio recording.')
                            : t('This assessment includes a passage reading section. You will need to read a passage aloud, and your audio will be recorded.')}
                      </p>
                      {micPermission !== 'granted' && (
                        <button
                          type="button"
                          onClick={requestMicPermission}
                          disabled={micPermission === 'requesting'}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60 transition-colors"
                        >
                          <MicrophoneIcon className="w-3.5 h-3.5" />
                          {micPermission === 'requesting' ? t('Requesting...') : t('Allow Microphone')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Camera permission for video proctoring */}
              {hasCameraMonitoring && (
                <div className={`rounded-xl border p-3 mb-4 sm:p-4 sm:mb-6 ${
                  cameraPermission === 'granted'
                    ? 'border-emerald-200 bg-emerald-50'
                    : cameraPermission === 'denied'
                      ? 'border-red-200 bg-red-50'
                      : 'border-blue-200 bg-blue-50'
                }`}>
                  <div className="flex gap-2 sm:gap-2.5">
                    <VideoCameraIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      cameraPermission === 'granted'
                        ? 'text-emerald-600'
                        : cameraPermission === 'denied'
                          ? 'text-red-600'
                          : 'text-blue-600'
                    }`} />
                    <div className="flex-1">
                      <h3 className={`text-xs font-bold mb-1 sm:text-sm ${
                        cameraPermission === 'granted'
                          ? 'text-emerald-900'
                          : cameraPermission === 'denied'
                            ? 'text-red-900'
                            : 'text-blue-900'
                      }`}>
                        {cameraPermission === 'granted'
                          ? t('Webcam access granted')
                          : cameraPermission === 'denied'
                            ? t('Webcam access denied')
                            : t('Webcam access required')}
                      </h3>
                      <p className={`text-xs mb-2 sm:text-sm ${
                        cameraPermission === 'granted'
                          ? 'text-emerald-800'
                          : cameraPermission === 'denied'
                            ? 'text-red-800'
                            : 'text-blue-800'
                      }`}>
                        {cameraPermission === 'granted'
                          ? t('Your webcam is ready. The proctoring session will be active during the assessment.')
                          : cameraPermission === 'denied'
                            ? t('Please allow webcam access in your browser settings and refresh the page. This assessment has video proctoring enabled.')
                            : t('This assessment has video proctoring enabled. You must grant webcam access to proceed.')}
                      </p>
                      {cameraPermission !== 'granted' && (
                        <button
                          type="button"
                          onClick={requestCameraPermission}
                          disabled={cameraPermission === 'requesting'}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60 transition-colors"
                        >
                          <VideoCameraIcon className="w-3.5 h-3.5" />
                          {cameraPermission === 'requesting' ? t('Requesting...') : t('Allow Webcam')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* CTA – full-width brand button; compact on mobile */}
              <button
                onClick={startAssessment}
                data-testid="start-assessment-btn"
                disabled={
                  timerExpired ||
                  (hasPassageReading && micPermission !== 'granted') ||
                  (hasCameraMonitoring && cameraPermission !== 'granted')
                }
                className="w-full flex items-center justify-center px-6 py-2.5 sm:py-3.5 bg-gradient-to-r from-electric-blue to-indigo-600 hover:from-electric-blue/90 hover:to-indigo-700 text-white text-sm sm:text-base font-bold rounded-lg shadow-lg shadow-electric-blue/20 hover:shadow-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {timerExpired
                  ? t("Time's up")
                  : hasPassageReading && micPermission !== 'granted'
                    ? t('Enable Microphone to Start')
                    : hasCameraMonitoring && cameraPermission !== 'granted'
                      ? t('Enable Webcam to Start')
                      : t('Start Assessment')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentJoin;
